import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRight } from 'lucide-react';
import VoiceTextInput from './VoiceTextInput';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// =====================================================================
// FULL FEEDBACK QUESTION BANK (Part 5)
// =====================================================================
const PENDING = {};

async function postFeedback(attemptId, body) {
  try {
    await fetch(`${API}/feedback-module/${attemptId}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
  } catch {}
}

// Saves any welcome answers given before the attempt was created
export async function flushPendingFeedback(attemptId) {
  const items = Object.values(PENDING);
  Object.keys(PENDING).forEach(k => delete PENDING[k]);
  await Promise.all(items.map(b => postFeedback(attemptId, b)));
}

const FB_BANK = {
  // WELCOME
  FB1: { id: 'FB1', checkpoint: 'welcome', question: 'What is your first impression of SPRING, before you begin?', type: 'text', optional: true },
  FB2: { id: 'FB2', checkpoint: 'welcome', question: 'This page gave me a clear idea of what the assessment involves and what happens next.', type: 'rating' },
  // SECTION CHECK-IN (shown after each section)
  FB3: { id: 'FB3', checkpoint: 'section', question: 'This section was easy to answer.', type: 'rating' },
  FB4: { id: 'FB4', checkpoint: 'section', question: 'The pace felt right. Not too fast, not too slow.', type: 'rating' },
  FB5: { id: 'FB5', checkpoint: 'section', question: 'Nothing in this section felt confusing.', type: 'rating' },
  FB6: { id: 'FB6', checkpoint: 'section', question: 'Nothing in this section felt intrusive or judgmental.', type: 'rating' },
  FB7: { id: 'FB7', checkpoint: 'section', question: 'The language used in this section was clear and plain, not full of jargon.', type: 'rating' },
  // Section 3 extras (between FB7 and FB8)
  FB7a: { id: 'FB7a', checkpoint: 'section_3_extra', question: 'The financial section was not as bad as I expected.', type: 'rating' },
  FB7b: { id: 'FB7b', checkpoint: 'section_3_extra', question: 'The calculator was easy to use.', type: 'rating' },
  // Continue section check-in
  FB8: { id: 'FB8', checkpoint: 'section', question: 'Was anything in this section confusing, intrusive, or judgmental?', type: 'yesno' },
  FB9: { id: 'FB9', checkpoint: 'section', question: 'If yes, what was it?', type: 'text', showIf: 'FB8_yes' },
  FB10: { id: 'FB10', checkpoint: 'section', question: 'Is there anything else about this section you want to tell us?', type: 'yesno' },
  FB11: { id: 'FB11', checkpoint: 'section', question: 'If yes, what?', type: 'text', showIf: 'FB10_yes' },
  FB12: { id: 'FB12', checkpoint: 'section', question: 'How did you feel at the end of this section?', type: 'mood' },
  FB13: { id: 'FB13', checkpoint: 'section', question: 'Anything you would like to add about how this section felt?', type: 'text', optional: true },
  // RESULTS AND PATHWAY (all shown regardless of result branch)
  FB14: { id: 'FB14', checkpoint: 'results_and_pathway', question: 'My suggested pathway felt accurate to me.', type: 'rating' },
  FB15: { id: 'FB15', checkpoint: 'results_and_pathway', question: 'The reasoning behind it made sense to me.', type: 'rating' },
  FB16: { id: 'FB16', checkpoint: 'results_and_pathway', question: 'It read like a recommendation, not a final decision made for me.', type: 'rating' },
  FB17: { id: 'FB17', checkpoint: 'results_and_pathway', question: 'I was shown two possible paths and asked to choose. That felt like a genuine choice.', type: 'rating' },
  FB18: { id: 'FB18', checkpoint: 'results_and_pathway', question: 'This result felt respectful, not like I had failed something.', type: 'rating' },
  FB19: { id: 'FB19', checkpoint: 'results_and_pathway', question: 'The earlier question about my vision for success was clear and easy to answer.', type: 'rating' },
  FB20: { id: 'FB20', checkpoint: 'results_and_pathway', question: 'The reasoning I was shown felt backed up by enough of my own answers.', type: 'rating' },
  FB21: { id: 'FB21', checkpoint: 'results_and_pathway', question: 'Was there anything about your result or suggested pathway that surprised you, or did not sit right?', type: 'text', optional: true },
  FB22: { id: 'FB22', checkpoint: 'results_and_pathway', question: 'On a scale of 1 to 5, how much did you want to continue with your suggested pathway?', type: 'rating' },
  FB23: { id: 'FB23', checkpoint: 'results_and_pathway', question: 'If you did not want to continue with your suggested pathway, why not?', type: 'text', optional: true },
  FB24: { id: 'FB24', checkpoint: 'results_and_pathway', question: 'On a scale of 1 to 5, how much did you want to take one of the other pathways instead?', type: 'rating' },
  FB25: { id: 'FB25', checkpoint: 'results_and_pathway', question: 'If you wanted a different pathway, which one, and why?', type: 'text', optional: true },
  FB26: { id: 'FB26', checkpoint: 'results_and_pathway', question: 'What would you be willing to pay per month for the programme to continue, as a monthly subscription?', type: 'single_select',
    note: 'We are testing this assessment with people around the world, so we are asking in US dollars to keep the answers comparable. Thank you.',
    options: ['Under USD $30 a month', 'USD $30 to $59 a month', 'USD $60 to $99 a month', 'USD $100 or more a month', 'I would not pay a monthly subscription'] },
  FB27: { id: 'FB27', checkpoint: 'results_and_pathway', question: 'Overall, the platform felt professional and trustworthy.', type: 'rating' },
  // FINAL OVERALL
  FB28: { id: 'FB28', checkpoint: 'overall', question: 'Overall, it was easy to use.', type: 'rating' },
  FB29: { id: 'FB29', checkpoint: 'overall', question: 'The design of the site felt well put together.', type: 'rating' },
  FB30: { id: 'FB30', checkpoint: 'overall', question: 'The wording throughout felt clear and well written.', type: 'rating' },
  FB31: { id: 'FB31', checkpoint: 'overall', question: 'Is there anything that would make the platform feel more professional or trustworthy?', type: 'text', optional: true },
  FB32: { id: 'FB32', checkpoint: 'overall', question: 'Anything else you want to tell us?', type: 'text', optional: true },
};

// Group mappings
export const WELCOME_FB = ['FB1', 'FB2'];
export const SECTION_FB_BASE = ['FB3', 'FB4', 'FB5', 'FB6', 'FB7', 'FB8', 'FB9', 'FB10', 'FB11', 'FB12', 'FB13'];
export const SECTION_3_EXTRAS = ['FB7a', 'FB7b'];
export const RESULTS_FB = ['FB14', 'FB15', 'FB16', 'FB17', 'FB18', 'FB19', 'FB20', 'FB21', 'FB22', 'FB23', 'FB24', 'FB25', 'FB26', 'FB27'];
export const OVERALL_FB = ['FB28', 'FB29', 'FB30', 'FB31', 'FB32'];

export function getSectionFBIds(sectionNum) {
  if (sectionNum === 3) {
    return ['FB3', 'FB4', 'FB5', 'FB6', 'FB7', 'FB7a', 'FB7b', 'FB8', 'FB9', 'FB10', 'FB11', 'FB12', 'FB13'];
  }
  return SECTION_FB_BASE;
}

/**
 * Renders a set of FB questions inline.
 * Props: attemptId, questionIds, sectionNum (for checkpoint prefix), resultType (for conditional display)
 */
export default function InlineFeedback({ attemptId, questionIds, sectionNum, resultType }) {
  const [responses, setResponses] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Before the attempt exists (access-code start) answers are held and saved once it is created
    if (!attemptId) { setLoaded(true); return; }
    (async () => {
      try {
        const checkpoint = sectionNum ? `section_${sectionNum}` : undefined;
        const url = checkpoint
          ? `${API}/feedback-module/${attemptId}?checkpoint=${checkpoint}`
          : `${API}/feedback-module/${attemptId}`;
        const res = await fetch(url);
        const data = await res.json();
        const map = {};
        for (const r of (data.responses || [])) {
          map[r.question_id] = r.rating_value ?? r.text_value;
        }
        setResponses(map);
      } catch {}
      setLoaded(true);
    })();
  }, [attemptId, sectionNum]);

  const saveResponse = useCallback(async (questionId, value, type, checkpoint, silent) => {
    // A silent save (on blur) keeps the layout still so the next click is not lost
    if (!silent) setResponses(prev => ({ ...prev, [questionId]: value }));
    const body = {
      question_id: questionId,
      checkpoint: sectionNum ? `section_${sectionNum}` : checkpoint,
      response_type: type,
      rating_value: type === 'rating' || type === 'mood' ? value : undefined,
      text_value: type === 'text' || type === 'single_select' ? value : (type === 'yesno' ? value : undefined),
    };
    if (!attemptId) { PENDING[questionId] = body; return; }
    await postFeedback(attemptId, body);
  }, [attemptId, sectionNum]);

  if (!loaded || !questionIds?.length) return null;

  const questions = questionIds.map(id => FB_BANK[id]).filter(Boolean);

  // Filter based on conditional logic (FB8/FB10 yes/no only)
  const visible = questions.filter(q => {
    if (q.showIf === 'FB8_yes') return responses['FB8'] === 'Yes';
    if (q.showIf === 'FB10_yes') return responses['FB10'] === 'Yes';
    return true;
  });

  return (
    <div className="inline-fb" data-testid="inline-feedback">
      {visible.map(q => (
        <InlineFBQuestion key={q.id} question={q} value={responses[q.id]}
          onSave={(val, silent) => saveResponse(q.id, val, q.type, q.checkpoint, silent)} />
      ))}
    </div>
  );
}

function InlineFBQuestion({ question, value, onSave }) {
  const [textDraft, setTextDraft] = useState('');
  const answered = value !== undefined && value !== null && value !== '';

  if (question.type === 'rating') {
    return (
      <div className={`ifb-q ${answered ? 'answered' : ''}`} data-testid={`fb-q-${question.id}`}>
        <p className="ifb-q-text">{question.question}</p>
        <div className="ifb-rating-btns">
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n} className={`ifb-rate-btn ${value === n ? 'active' : ''}`}
              onClick={() => onSave(n)} data-testid={`fb-rate-${question.id}-${n}`}>{n}</button>
          ))}
        </div>
        <div className="ifb-scale-labels"><span>Strongly disagree</span><span>Strongly agree</span></div>
      </div>
    );
  }

  if (question.type === 'mood') {
    return (
      <div className={`ifb-q ${answered ? 'answered' : ''}`} data-testid={`fb-q-${question.id}`}>
        <p className="ifb-q-text">{question.question}</p>
        <div className="ifb-rating-btns">
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n} className={`ifb-rate-btn ${value === n ? 'active' : ''}`}
              onClick={() => onSave(n)} data-testid={`fb-rate-${question.id}-${n}`}>{n}</button>
          ))}
        </div>
        <div className="ifb-scale-labels"><span>Not great</span><span>Really good</span></div>
      </div>
    );
  }

  if (question.type === 'yesno') {
    return (
      <div className={`ifb-q ${answered ? 'answered' : ''}`} data-testid={`fb-q-${question.id}`}>
        <p className="ifb-q-text">{question.question}</p>
        <div className="ifb-choice-btns" style={{ flexDirection: 'row', gap: 8 }}>
          <button className={`ifb-choice-btn ${value === 'Yes' ? 'active' : ''}`} onClick={() => onSave('Yes')}>Yes</button>
          <button className={`ifb-choice-btn ${value === 'No' ? 'active' : ''}`} onClick={() => onSave('No')}>No</button>
        </div>
      </div>
    );
  }

  if (question.type === 'single_select') {
    return (
      <div className={`ifb-q ${answered ? 'answered' : ''}`} data-testid={`fb-q-${question.id}`}>
        <p className="ifb-q-text">{question.question}</p>
        {question.note && <p style={{ fontSize: 12, color: '#888', marginBottom: 10, lineHeight: 1.4 }}>{question.note}</p>}
        <div className="ifb-choice-btns">
          {question.options.map(opt => (
            <button key={opt} className={`ifb-choice-btn ${value === opt ? 'active' : ''}`}
              onClick={() => onSave(opt)} data-testid={`fb-select-${question.id}`}>{opt}</button>
          ))}
        </div>
      </div>
    );
  }

  // Text type
  return (
    <div className={`ifb-q ${answered ? 'answered' : ''}`} data-testid={`fb-q-${question.id}`}>
      <p className="ifb-q-text">{question.question}{question.optional ? '' : ''}</p>
      {answered ? (
        <p className="ifb-q-saved">"{value}"</p>
      ) : (
        <VoiceTextInput value={textDraft} onChange={setTextDraft} placeholder="Type or speak..."
          data-testid={`fb-text-${question.id}`}
          onKeyDown={e => { if (e.key === 'Enter' && textDraft.trim()) { onSave(textDraft.trim()); setTextDraft(''); } }}
          onBlur={() => { if (textDraft.trim()) onSave(textDraft.trim(), true); }} />
      )}
      {!answered && textDraft.trim() && (
        <button className="ifb-text-send-btn" onClick={() => { onSave(textDraft.trim()); setTextDraft(''); }}
          style={{ marginTop: 6 }}>Submit</button>
      )}
    </div>
  );
}
